import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { CheckCheck, MessageCircle, AtSign, Mail, MessageSquare, Filter, RefreshCw } from 'lucide-react';
import { TopNav } from '@/components/layout/TopNav';
import { inboxApi, type InboxItem } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { useBackgroundRefresh } from '@/hooks/useBackgroundRefresh';
import { formatRelativeTime, cn } from '@/lib/utils';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';

const TYPE_ICONS: Record<string, typeof MessageCircle> = {
  comment: MessageCircle,
  mention: AtSign,
  dm: Mail,
  reply: MessageSquare,
};

// Shown for items stored without text (e.g. photo/sticker comments synced
// before attachments were labelled).
const NO_TEXT = '(No text — photo, sticker or GIF)';

export function Inbox() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();

  const [platform, setPlatform] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [read, setRead] = useState<string>('');
  const [selected, setSelected] = useState<InboxItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['inbox', currentWorkspace?.id, platform, type, read],
    queryFn: async () => {
      const token = await getToken();
      return inboxApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        platform: platform || undefined,
        type: type || undefined,
        read: read || undefined,
        limit: 100,
      });
    },
    enabled: !!currentWorkspace,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return inboxApi.markRead(token!, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', currentWorkspace?.id] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return inboxApi.markAllRead(token!, currentWorkspace!.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox', currentWorkspace?.id] }),
  });

  const [replyText, setReplyText] = useState('');
  const sendReply = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const data = await inboxApi.reply(token!, selected!.id, replyText.trim());
      // Never treat a reply the platform didn't accept as sent — keep the draft.
      if (!data.success) throw new Error('Reply failed — try again.');
      return data;
    },
    onSuccess: () => {
      setReplyText('');
      qc.invalidateQueries({ queryKey: ['inbox', currentWorkspace?.id] });
    },
  });

  // The page stays mounted when the workspace switcher changes brand: drop the
  // previous brand's open item and draft so a reply can't go out from its account.
  useEffect(() => {
    setSelected(null);
    setReplyText('');
    sendReply.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  const REPLYABLE_PLATFORMS = ['x', 'facebook', 'instagram'];
  const canReply = selected && REPLYABLE_PLATFORMS.includes(selected.platform);

  const syncNow = useBackgroundRefresh({
    trigger: async () => {
      const token = await getToken();
      return inboxApi.sync(token!, currentWorkspace!.id);
    },
    queryKeys: [['inbox', currentWorkspace?.id]],
    durationMs: 15_000,
  });

  const items = data?.items ?? [];
  const unreadCount = items.filter((i) => !i.read_at).length;

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <TopNav title="Inbox" />

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel: list */}
        <div className={cn('w-full md:w-80 md:border-r border-border flex-col shrink-0', selected ? 'hidden md:flex' : 'flex')}>
          {/* Filters */}
          <div className="p-3 space-y-2 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">Filters</span>
              </div>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead.mutate()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <CheckCheck className="h-3.5 w-3.5" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => syncNow.start()}
                  disabled={syncNow.isRefreshing}
                  className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 disabled:opacity-50"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', syncNow.isRefreshing && 'animate-spin')} />
                  {syncNow.isRefreshing ? 'Syncing…' : 'Sync now'}
                </button>
              </div>
            </div>
            {syncNow.isRefreshing && (
              <p className="text-xs text-muted-foreground">
                Checking your connected accounts for new mentions & messages…
              </p>
            )}
            {syncNow.isError && (
              <p className="text-xs text-destructive">Sync failed. Try again.</p>
            )}
            <div className="flex gap-1.5">
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="flex-1 bg-input border border-border rounded-md px-2 py-1 text-xs text-foreground outline-none"
              >
                <option value="">All platforms</option>
                {Object.entries(PLATFORMS).map(([id, p]) => (
                  <option key={id} value={id}>{p.label}</option>
                ))}
              </select>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="flex-1 bg-input border border-border rounded-md px-2 py-1 text-xs text-foreground outline-none"
              >
                <option value="">All types</option>
                <option value="comment">Comments</option>
                <option value="mention">Mentions</option>
                <option value="dm">DMs</option>
                <option value="reply">Replies</option>
              </select>
            </div>
            <div className="flex gap-1">
              {['', 'false', 'true'].map((v) => (
                <button
                  key={v}
                  onClick={() => setRead(v)}
                  className={cn(
                    'flex-1 py-1 text-xs rounded-md transition-colors',
                    read === v
                      ? 'bg-accent/10 text-accent'
                      : 'text-muted-foreground hover:bg-muted/40'
                  )}
                >
                  {v === '' ? 'All' : v === 'false' ? 'Unread' : 'Read'}
                </button>
              ))}
            </div>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 space-y-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-xs text-muted-foreground">No inbox items</p>
              </div>
            ) : (
              items.map((item) => {
                const TypeIcon = TYPE_ICONS[item.type] ?? MessageCircle;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSelected(item);
                      if (!item.read_at) markRead.mutate(item.id);
                    }}
                    className={cn(
                      'w-full flex items-start gap-3 px-3 py-3 border-b border-border text-left hover:bg-muted/40 transition-colors',
                      selected?.id === item.id && 'bg-muted/50',
                      !item.read_at && 'border-l-2 border-l-accent'
                    )}
                  >
                    <div className="relative h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-tertiary ring-1 ring-border text-muted-foreground">
                      <TypeIcon className="h-3.5 w-3.5" />
                      <span className="absolute -bottom-0.5 -right-1 inline-flex rounded-[5px] ring-2 ring-canvas">
                        <PlatformIcon platform={item.platform} size="xs" labelled />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-medium text-foreground truncate">
                          {item.author_name ?? 'Unknown'}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatRelativeTime(item.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {item.content || NO_TEXT}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel: detail */}
        <div className={cn('flex-1 items-center justify-center overflow-y-auto', selected ? 'flex' : 'hidden md:flex')}>
          {selected ? (
            <div className="p-6 max-w-lg w-full">
              <button
                onClick={() => setSelected(null)}
                className="md:hidden mb-4 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                &larr; Back to inbox
              </button>
              <div className="flex items-start gap-3 mb-4">
                {selected.author_avatar ? (
                  <img
                    src={selected.author_avatar}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-hairline"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-tertiary ring-1 ring-border">
                    <span className="text-muted-foreground text-sm font-medium">
                      {selected.author_name?.[0]?.toUpperCase() ?? '?'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-foreground">{selected.author_name ?? 'Unknown'}</p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground capitalize">
                    <span>{selected.type}</span>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1">
                      <PlatformIcon platform={selected.platform} size="xs" variant="glyph" />
                      {PLATFORMS[selected.platform as keyof typeof PLATFORMS]?.label ?? selected.platform}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(selected.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="bg-tertiary ring-1 ring-border rounded-lg p-4">
                <p className="text-sm text-foreground">{selected.content || NO_TEXT}</p>
              </div>
              {canReply ? (
                <div className="mt-4 space-y-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={`Reply as your ${PLATFORMS[selected.platform as keyof typeof PLATFORMS]?.label ?? selected.platform} account…`}
                    rows={3}
                    className="w-full bg-input border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:ring-1 focus:ring-accent"
                  />
                  <div className="flex items-center justify-between">
                    {sendReply.isError ? (
                      <p className="text-xs text-destructive">
                        {(sendReply.error as Error).message || 'Reply failed — try again.'}
                      </p>
                    ) : sendReply.isSuccess ? (
                      <p className="text-xs text-success">Reply sent</p>
                    ) : (
                      <span />
                    )}
                    <button
                      onClick={() => sendReply.mutate()}
                      disabled={!replyText.trim() || sendReply.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors"
                    >
                      {sendReply.isPending && <RefreshCw className="h-3 w-3 animate-spin" />}
                      Reply
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">
                  Replies aren't supported for this platform yet.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Select an item to view details</p>
          )}
        </div>
      </div>
    </div>
  );
}
